<template>
  <div :id="chartContainerId" :class="namespaceClass('__line-chart')"></div>
</template>

<script>
import d3 from 'd3';
import ResizeObserver from 'resize-observer-polyfill';
import HypoVueBaseMixin from 'hypo-vue-base-mixin';
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import HypoLineChart from './extends/hypo-line-chart';
import defaultConfig from './_config';

export default {
  name: 'hypo-growth-chart',
  mixins: [MwcVueHelperMixin, MwcConfigMixin, HypoVueBaseMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    config: {
      type: [String, Object],
      default: () => {},
    },
    colors: [Array, String],
    dottedLines: [Array, String],
    modelData: [Array, String],
    currencySymbol: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      timelineLevel: 'month',
      defaultConfig: {
        intlNamespace: this.mwcId,
        ...defaultConfig,
      },
    };
  },
  computed: {
    normalizedData() {
      return typeof this.modelData === 'string' ? JSON.parse(this.modelData) : this.modelData;
    },
    lineChartData() {
      return this.formatLineChartData(this.normalizedData || []);
    },
    configSettings() {
      return this.getComponentConfig().settings;
    },
    chartColors() {
      if (this.colors) {
        return typeof this.colors === 'string' ? JSON.parse(this.colors) : this.colors;
      }
      return this.configSettings.lineChartSettings.colors;
    },
    benchmarkLines() {
      if (this.dottedLines) {
        return typeof this.dottedLines === 'string'
          ? JSON.parse(this.dottedLines)
          : this.dottedLines;
      }
      return this.configSettings.lineChartSettings.dottedLines;
    },
    xTickValues() {
      return this.getXTickValues();
    },
  },
  watch: {
    modelData() {
      this.drawLineChart(this.lineChartData);
    },
    colors() {
      this.drawLineChart(this.lineChartData);
    },
    dottedLines() {
      this.drawLineChart(this.lineChartData);
    },
  },
  created() {
    this.chartContainerId = `line-chart-container--${this.guid()}`;
  },
  mounted() {
    this.drawLineChart(this.lineChartData);
    const myObserver = new ResizeObserver(entries => {
      entries.map(() => {
        this.handleResize();
      });
    });
    myObserver.observe(this.$el);
  },
  beforeDestroy() {
    this.hideTooltip();
  },
  methods: {
    hideTooltip() {
      if (this.chart) {
        const tooltip = this.chart.get('tooltipInfo');
        if (tooltip) {
          tooltip.hide();
        }
      }
    },
    checkAbbreviation(value) {
      const units = ['millionsDecimal', 'billionsDecimal', 'trillionsDecimal'];
      if (value >= 1e6) {
        const unit = Math.floor(Math.log(value) / Math.log(1000));
        return units[unit - 2];
      }
      return null;
    },
    getValueFormat(value, lineChartSettings) {
      const abbreviation = this.checkAbbreviation(value);
      if (abbreviation) {
        return this.formatter.format(value, abbreviation, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      return this.formatter.format(value, lineChartSettings.y1TickFormatAlias);
    },
    initializeLineChartSettings() {
      const that = this;
      const { lineChartSettings } = this.configSettings;
      const customSettings = {
        colors: this.chartColors,
        dottedLines: this.benchmarkLines,
        tooltipTimeFormat(date) {
          return that.formatter.format(date, lineChartSettings.tooltipTimeFormatAlias);
        },
        legendValuesFormat(value) {
          return that.currencySymbol + that.getValueFormat(value, lineChartSettings);
        },
        xTickFormat(date) {
          return that.formatter.format(date, lineChartSettings.xTickFormatAlias);
        },
        y1TickFormat(value) {
          return that.getValueFormat(value, lineChartSettings);
        },
      };
      this.chart = new HypoLineChart({
        ...lineChartSettings,
        ...customSettings,
        xTickValues: this.xTickValues,
        timelineLevel: this.timelineLevel,
      });
    },
    drawLineChart(lineChartData) {
      const chartContainer = d3.select(`#${this.chartContainerId}`);
      chartContainer.selectAll('*').remove();
      chartContainer.attr({
        class: this.namespaceClass('__line-chart'),
        id: this.chartContainerId,
      });
      if (lineChartData.length > 0) {
        this.initializeLineChartSettings();
        lineChartData.forEach((data, index) => {
          if (data && data.length > 0) {
            if (index === 0) {
              this.chart.get('data').clean();
              chartContainer
                .datum({
                  y1: data,
                })
                .call(this.chart.init());
            } else {
              this.chart.add(data[0].name, data, data[0].groupName);
            }
          }
        });
      } else {
        this.chart = new HypoLineChart({
          ...this.configSettings.emptyChartSetting,
        });
        chartContainer
          .datum({
            y1: [],
          })
          .call(this.chart.init());
      }
      this.hideTooltip();
    },
    formatLineChartData(lineChartData) {
      const newLineChartData = [];
      lineChartData.forEach(item => {
        const obj = [];
        const { name } = item;
        item.data.forEach(data => {
          obj.push({
            name,
            value: data.value,
            date: new Date(data.date),
            groupName: 'y1',
          });
        });
        newLineChartData.push(obj);
      });
      return newLineChartData;
    },
    handleResize() {
      if (
        document.querySelector(`#${this.chartContainerId}`) &&
        this.chart.get('canvas') !== null
      ) {
        this.chart.update();
      }
    },
    getXTickValues() {
      let dates = [];
      this.normalizedData.forEach(item => {
        item.data.forEach(i => {
          dates.push(i.date);
        });
      });

      dates = dates.sort((a, b) => {
        return new Date(a).getTime() - new Date(b).getTime();
      });
      const { length } = dates;
      const startDate = new Date(dates[0]);
      const endDate = new Date(dates[length - 1]);
      let ticks = [];
      const monthsDiff =
        12 -
        startDate.getMonth() +
        (endDate.getMonth() + 1) +
        (endDate.getFullYear() - startDate.getFullYear() - 1) * 12;
      if (monthsDiff > 12 * 3 + 1) {
        this.timelineLevel = 'year';
        ticks = this.getTicksByYear(dates);
      } else {
        dates.forEach((date, index) => {
          const currentDate = new Date(date);
          if (index === 0) {
            ticks.push(currentDate);
          } else {
            const lastDate = ticks[ticks.length - 1];
            if (
              currentDate.getFullYear() === lastDate.getFullYear() &&
              currentDate.getMonth() === lastDate.getMonth()
            ) {
              if (index === length - 1) {
                ticks.pop();
                ticks.push(currentDate);
              }
            } else {
              const nextMonthStartDate = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() + 1,
                1
              );
              const currentMonthEndDate = new Date(nextMonthStartDate.getTime() - 60 * 60 * 1000);
              ticks.push(currentMonthEndDate);
            }
          }
        });
        if (monthsDiff <= 13) {
          this.timelineLevel = 'month';
        } else if (monthsDiff <= 12 * 3 + 1) {
          this.timelineLevel = 'quarter';
          ticks = ticks.filter((item, index) => {
            return (
              index === 0 ||
              index === ticks.length - 1 ||
              [1, 4, 7, 10].indexOf(item.getMonth() + 1) > -1
            );
          });
        }
      }
      return ticks;
    },
    getTicksByYear(dates) {
      // yearsDiff : step
      //    0 - 11 : 1
      //   12 - 20 : 2
      //   21 - 30 : 3
      //   31 - 40 : 5
      //     >= 40 : 10
      const startDate = new Date(dates[0]);
      const endDate = new Date(dates[dates.length - 1]);
      const yearDiff = endDate.getFullYear() - startDate.getFullYear();

      const years = [];
      let ticks = [];
      dates.forEach(date => {
        const currentDate = new Date(date);
        if (years.indexOf(currentDate.getFullYear()) === -1) {
          ticks.push(currentDate);
          years.push(currentDate.getFullYear());
        }
      });

      if (yearDiff > 11) {
        let step;
        if (yearDiff < 21) {
          step = 2;
        } else if (yearDiff < 31) {
          step = 3;
        } else if (yearDiff < 41) {
          step = 5;
        } else {
          step = 10;
        }
        ticks = ticks.filter((item, index) => index % step === 0);
      }
      return ticks;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@mds/constants';
@import './styles/growth-chart.scss';
</style>
